import React, { memo, useCallback, useState, useMemo, useRef } from 'react';
import { getCountryFlag } from '@/pages/home/_config/countries';
// import JumperBtn from '../../../JumperBtn';
import StackBarEcharts from '../../../Echart/StackBarEcharts';
import PieEchart from '../../../Echart/PieEchart';
import GeneralLegend from '@/pages/intelligence/components/GeneralLegend';
import useLegends from './hooks/useLegends';
import Swiper from '../Swiper';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import './index.scss';
import { getLocale } from 'umi';
import { LANGUAGE_CN } from '@/pages/intelligence/_config/config';

const legendNameFormatter = (name: string, cfg: { isCN: boolean; legends: any[] }): string => {
  const { isCN, legends } = cfg ?? {};
  const legend = legends?.find((legend) => legend?.name === name);
  const legendText = (isCN ? legend?.nameCN : legend?.name) || legend?.name || name;
  return legendText === 'other' ? 'Others' : legendText;
};

interface PageJumperPropsTypes {
  showCountryIcon?: boolean; // 是否展示国家icon
  chartType: ChartType; // 渲染哪种组件，key与dataList中的一致
  dataList: any;
  tooltipHeaderRightTitle?: string;
  [keyName: string]: any;
}

const DEFAULT_PROPS: PageJumperPropsTypes = {
  showCountryIcon: false,
  chartType: ChartType.BarHorizontal,
  dataList: null,
  tooltipHeaderRightTitle: '',
};

const PageJumper: React.FC<PageJumperPropsTypes> = (props) => {
  const { showCountryIcon, chartType, dataList, tooltipHeaderRightTitle } = props;

  const { legends, legendsSelected, handleLegendsSelectedChange, filterListByLegendsSelected } =
    useLegends({
      chartType,
      dataList,
    });

  const locale = getLocale();
  const isCN = locale === LANGUAGE_CN;

  const barCharts = useRef<IGlobalViewPieEChartInstance[]>([]);
  const pieCharts = useRef<IGlobalViewPieEChartInstance[]>([]);

  const list = useMemo(() => {
    const fieldsMap: any = {
      [ChartType.BarHorizontal]: 'bars',
      [ChartType.Pie]: 'pies',
    };
    return dataList?.[fieldsMap?.[chartType]];
  }, [chartType, dataList]);

  // 显示数据的开始索引与结束索引
  const [showIndex, setShowIndex] = useState<{
    startIndex: number;
    endIndex: number;
  }>({
    startIndex: -1,
    endIndex: -1,
  });
  // 一行显示多少个元素
  const [lineCount, setLineCount] = useState<number>(0);
  // 每个元素间的右间距
  const [itemMarginRight, setItemMarginRight] = useState<number>(0);
  // const [pageSize, setPageSize] = useState<number>(0);

  // 元素右间距最小 30px
  const itemMarginRightWithMin = useMemo(
    () => (itemMarginRight < 36 ? 36 : itemMarginRight),
    [itemMarginRight],
  );

  const handlePageChange = useCallback(
    ({ pageIndex, pageSize, lineCount, showCount, containerWidth }) => {
      // setPageSize(pageSize);
      setShowIndex({
        startIndex: (pageIndex - 1) * showCount || 0,
        endIndex: pageIndex * showCount - 1,
      });
      setLineCount(lineCount);
      setItemMarginRight((containerWidth - 330 * lineCount - 46 * 2) / (lineCount - 1));
    },
    [],
  );

  const renderEChartItem = useCallback(
    (
      chartType: ChartType,
      listData: any,
      tooltipPosition: 'left' | 'right',
      barRefs: any,
      pieRefs: any,
      i: number,
    ) => {
      const list = filterListByLegendsSelected(chartType, legendsSelected, listData);
      // 饼状图
      if (chartType === ChartType.Pie)
        return (
          <PieEchart
            ref={(ref) => {
              if (!Array.isArray(pieRefs?.current)) return;
              pieRefs.current[i] = ref;
            }}
            data={list}
            tooltipPosition={tooltipPosition}
            tooltipHeaderRightTitle={tooltipHeaderRightTitle}
            ignoreSortNames={['other']}
            fontSize={14}
          />
        );
      // 横向柱状图
      const firstData = list?.[0];
      const countryIcon = getCountryFlag(firstData?.themeKey);
      const themeText = (isCN ? firstData?.themeCN : firstData?.theme) || firstData?.theme;
      return (
        <>
          <h3 className="dbi-page-jumper-item-title">
            {showCountryIcon && countryIcon && (
              <img className="dbi-top-market-genre-bar-country-icon" src={countryIcon} alt="" />
            )}{' '}
            {themeText || '-'}
          </h3>
          <StackBarEcharts
            ref={(ref) => {
              if (!Array.isArray(barRefs?.current)) return;
              barRefs.current[i] = ref;
            }}
            data={list}
            tooltipPosition={tooltipPosition}
            tooltipHeaderRightTitle={tooltipHeaderRightTitle}
            ignoreSortNames={['other']}
            fontSize={12}
          />
        </>
      );
    },
    [legendsSelected, showCountryIcon, tooltipHeaderRightTitle],
  );

  const getCharts = (): IGlobalViewPieEChartInstance[] => {
    const chartsMapping: any = {
      [ChartType.BarHorizontal]: barCharts?.current,
      [ChartType.Pie]: pieCharts?.current,
    };
    return chartsMapping?.[chartType] || [];
  };

  const handleMouseEnter = (payload: any) => {
    const charts = getCharts();
    for (const chart of charts) {
      if (!chart) continue;
      chart?.onHighlight(payload);
    }
  };

  const handleMouseLeave = (payload: any) => {
    const charts = getCharts();
    for (const chart of charts) {
      if (!chart) continue;
      chart?.onDownplay(payload);
    }
  };

  return (
    <section className="dbi-page-jumper">
      <Swiper dataLen={list?.length} minimumColMargin={36} onChange={handlePageChange}>
        <hgroup
          className="dbi-page-jumper-container"
          style={{
            // marginBottom: pageSize < 2 ? 0 : 16,
            marginBottom: 16,
          }}
        >
          {/* 第一行 */}
          <section className="dbi-page-jumper-first-line-box">
            {Array.isArray(list) &&
              list?.map((t, i) => {
                const isShow =
                  showIndex?.startIndex >= 0 &&
                  i >= showIndex?.startIndex &&
                  showIndex?.endIndex >= 0 &&
                  i <= showIndex?.endIndex;
                const firstLineEndIndex = showIndex?.startIndex + lineCount - 1; // 第一行结束索引
                const isShowFirstLinePosition = firstLineEndIndex >= 0 && i <= firstLineEndIndex; // 当前展示的是否在第一行
                // const isFillFirstLine = list?.length - 1 < firstLineEndIndex; // 第一行是否能填充满
                // const isFillSecondLine = list?.length - 1 < showIndex?.endIndex; // 第二行是否能填充满

                return (
                  isShow &&
                  isShowFirstLinePosition && (
                    <section
                      key={i}
                      className="dbi-page-jumper-item"
                      style={{ marginRight: i === firstLineEndIndex ? 0 : itemMarginRightWithMin }}
                    >
                      {renderEChartItem(
                        chartType,
                        t,
                        i === firstLineEndIndex ? 'left' : 'right',
                        barCharts,
                        pieCharts,
                        i,
                      )}
                    </section>
                  )
                );
              })}
          </section>
          {/* 第二行 */}
          <section className="dbi-page-jumper-second-line-box">
            {Array.isArray(list) &&
              list?.map((t, i) => {
                const isShow =
                  showIndex?.startIndex >= 0 &&
                  i >= showIndex?.startIndex &&
                  showIndex?.endIndex >= 0 &&
                  i <= showIndex?.endIndex;
                const firstLineEndIndex = showIndex?.startIndex + lineCount - 1; // 第一行结束索引
                const isShowFirstLinePosition = firstLineEndIndex >= 0 && i <= firstLineEndIndex; // 当前展示的是否在第一行

                return (
                  isShow &&
                  !isShowFirstLinePosition && (
                    <section
                      key={i}
                      className="dbi-page-jumper-item"
                      style={{
                        marginTop: 30,
                        marginRight: i === showIndex?.endIndex ? 0 : itemMarginRightWithMin,
                      }}
                    >
                      {renderEChartItem(
                        chartType,
                        t,
                        i === showIndex?.endIndex ? 'left' : 'right',
                        barCharts,
                        pieCharts,
                        i,
                      )}
                    </section>
                  )
                );
              })}
          </section>
          {/* 这种方案在屏幕缩放的时候卡片因为需要动态计算marginRight而布局会换行抖动 */}
          {/* {Array.isArray(list) && list?.map((_, i) => {
            const isShow = (showIndex?.startIndex >= 0 && i >= showIndex?.startIndex) && (showIndex?.endIndex >= 0 && i <= showIndex?.endIndex);
            const firstLineEndIndex = showIndex?.startIndex + lineCount - 1; // 第一行结束索引
            const isShowFirstLinePosition = firstLineEndIndex >= 0 && i <= firstLineEndIndex; // 当前展示的是否在第一行
            // const isFillFirstLine = list?.length - 1 < firstLineEndIndex; // 第一行是否能填充满
            // const isFillSecondLine = list?.length - 1 < showIndex?.endIndex; // 第二行是否能填充满

            return isShow && (
              <section
                key={i}
                className="dbi-page-jumper-item"
                style={{
                  marginTop: isShowFirstLinePosition ? 0 : 30,
                  marginRight: (i === firstLineEndIndex || i === showIndex?.endIndex) ? 0 : itemMarginRight,
                }}
              >{i}</section>
            );
          })} */}
        </hgroup>
      </Swiper>
      <GeneralLegend
        className="dbi-top-market-genre-core__general-legend"
        formatter={(name: string) => legendNameFormatter(name, { isCN, legends })}
        options={legends}
        value={legendsSelected}
        onChange={handleLegendsSelectedChange}
        onMouseEnter={handleMouseEnter}
        onMouseOver={handleMouseEnter}
        onMouseMove={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
    </section>
  );
};

PageJumper.defaultProps = DEFAULT_PROPS;

export default memo(PageJumper);
